import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { ESPACE_PRIVE } from '../../constants/routing';
import { SeoAttributes } from '../../models/seo';
import { getRouteKeyFromSlug } from '../../utils/routes';
import { addAnalytic, AnalyticData, PageViewData } from './utils';

function isEspacePriveSegment(locale: string, segment?: string): boolean {
  if (!segment) return false;
  // keep the untranslated key: DataLayer values may be contributed with the FR slug
  return segment === ESPACE_PRIVE || getRouteKeyFromSlug(locale, segment) === ESPACE_PRIVE;
}

export type PageView = {
  pageName?: string | null;
  pageType: string;
  language: string;
  attributes?: SeoAttributes['Seo'];
};

const MIN_PAGE_CHAPTER_LEVELS = 2;
const PAGE_CHAPTER_KEY_REGEX = /^page_chapter(\d+)$/;

function getPageChapterLevel(page?: Record<string, string>): number {
  return Object.keys(page ?? {}).reduce((max, key) => {
    const match = PAGE_CHAPTER_KEY_REGEX.exec(key);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
}

/**
 * GTM keeps every variable already pushed, because the site does not reload pages.
 * Send every page_chapter level (at least MIN_PAGE_CHAPTER_LEVELS, and all levels already
 * sent in a previous pageview) with an empty value, so a previous value is always overwritten.
 */
function fillEmptyPageChapters(page: PageViewData['page']): void {
  const previousLevel = (globalThis.dataLayer ?? [])
    .filter((item) => item.event === 'pageview')
    .reduce((max, item) => Math.max(max, getPageChapterLevel(item.page)), 0);
  const levelCount = Math.max(MIN_PAGE_CHAPTER_LEVELS, previousLevel, getPageChapterLevel(page));

  for (let level = 1; level <= levelCount; level++) {
    const key: `page_chapter${number}` = `page_chapter${level}`;
    page[key] ??= '';
  }
}

export const usePageViewReport = ({ pageName, pageType, language, attributes }: PageView): void => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pageName) return;
    const page: PageViewData['page'] = {
      path: pathname,
      pagename: pageName,
      pagetype: pageType,
      language
    };

    const fullPath = pathname;
    let breadcrumbs;
    const dataLayer = attributes?.DataLayer;

    // en fonction du pagetype on peut avoir des chapters custom
    switch (pageType) {
      case 'mon_compte':
        page.page_chapter1 = 'account';
        break;
      case 'evenement':
        page.page_chapter1 = 'agenda';
        break;
      default:
        /* si un datalayer est définit cela veut dire que le client veux des valeurs customs pour la page */
        if (dataLayer) {
          breadcrumbs = dataLayer.split('/').filter((crumb) => crumb !== '');
        } else {
          // on enlève la langue du breadcrumb
          breadcrumbs = fullPath
            .split('/')
            .filter((crumb) => crumb !== '')
            .slice(1);
        }

        // special case for espace-prive (translated slug, e.g. private-space in EN)
        if (isEspacePriveSegment(language, breadcrumbs[0])) {
          breadcrumbs.shift();
        }

        breadcrumbs.forEach((crumb, index) => {
          page[`page_chapter${index + 1}`] = crumb;
        });
        break;
    }

    fillEmptyPageChapters(page);

    const data: AnalyticData = {
      event: 'pageview',
      page
    };
    addAnalytic(data);
  }, [pageName]);
};
